import { useState } from "react";
import { Check, PencilLine } from "lucide-react";
import { ReplyText } from "../../agent/rail/RailParts";
import AddPostForm, { type PillarChoice } from "./AddPostForm";
import { dayMonthShort } from "./calendarModel";
import type { NewPost } from "./MonthGrid";

type Kind = "planned" | "quick";

const KINDS: { value: Kind; label: string }[] = [
  { value: "planned", label: "Planned: three ideas, then a creative brief" },
  { value: "quick", label: "Quick: straight to production, no brief" },
];

const QUESTION = "What kind of post is it?";

/**
 * Add a post, in the side chat (GWLvd, bgsgr, qFzT7; DESIGN.md "Adding a post
 * starts from its day"). The rail's question card grammar: two tabs, the day
 * (already answered by the day that was clicked) and the kind, then Skip and
 * Next. "Or describe the post in a line" becomes the working title. Rendered
 * locally in the rail through agentRail.ask: no model call. Next opens the
 * post's own fields (title, day, format), and submitting runs the page's
 * existing add-post or quick-post mutation.
 */
export default function AddPostChatCard({
  year,
  month,
  day,
  pillars,
  onAdd,
  onAddQuick,
  onQuickDialog,
  onDone,
}: {
  year: number;
  /** 1-12. */
  month: number;
  day: number;
  pillars?: PillarChoice[];
  onAdd: (post: NewPost) => Promise<void> | void;
  /** Creates a quick post straight from the card's fields. */
  onAddQuick?: (post: NewPost) => Promise<void> | void;
  /** Without onAddQuick: hands a quick post over to the quick post dialog. */
  onQuickDialog?: (day: number) => void;
  onDone: () => void;
}) {
  const quickable = !!onAddQuick || !!onQuickDialog;
  const [kind, setKind] = useState<Kind | null>("planned");
  const [line, setLine] = useState("");
  const [tab, setTab] = useState<0 | 1>(1);
  const [step, setStep] = useState<1 | 2>(quickable ? 1 : 2);
  const chosen = KINDS.find((entry) => entry.value === kind) ?? null;
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  const dayLabel = dayMonthShort({ weekday, day, month });
  // A described post is a planned one: the line is its working title.
  const ready = !!kind || line.trim().length > 0;

  const next = () => {
    if (!ready) return;
    if (!kind) setKind("planned");
    if (kind === "quick" && !onAddQuick && onQuickDialog) {
      onDone();
      onQuickDialog(day);
      return;
    }
    setStep(2);
  };

  if (step === 1) {
    return (
      <div className="flex flex-col gap-3.5">
        <ReplyText content="A planned post gets three ideas, then a creative brief. A quick post goes straight to production." />
        <section className="m7r-card" data-live aria-label="The agent is asking">
          <div className="m7r-card-tabs" role="tablist" aria-label="Questions">
            {[`Day · ${dayLabel}`, "Kind"].map((header, i) => (
              <button
                key={header}
                type="button"
                role="tab"
                id={`add-post-tab-${i}`}
                aria-selected={tab === i}
                aria-controls={`add-post-panel-${i}`}
                tabIndex={tab === i ? 0 : -1}
                className="m7r-card-tab"
                data-answered={i === 0 || ready || undefined}
                onClick={() => setTab(i as 0 | 1)}
              >
                <i aria-hidden />
                <span>{header}</span>
              </button>
            ))}
          </div>
          <div role="tabpanel" id={`add-post-panel-${tab}`} aria-labelledby={`add-post-tab-${tab}`}>
            {tab === 0 ? (
              <div className="m7r-card-head">
                <p className="m7r-card-question">Which day is it on?</p>
                <p className="m7r-card-hint">{dayLabel}, from the day you picked. Change it on the next step.</p>
              </div>
            ) : (
              <>
                <div className="m7r-card-head">
                  <p className="m7r-card-question">{QUESTION}</p>
                  <p className="m7r-card-hint">Pick one, question 2 of 2</p>
                </div>
                <div role="radiogroup" aria-label={QUESTION}>
                  {KINDS.map((option) => {
                    const on = kind === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        role="radio"
                        aria-checked={on}
                        className="m7r-option"
                        data-on={on || undefined}
                        onClick={() => setKind(option.value)}
                      >
                        <span className="m7r-pick" data-kind="radio" data-on={on || undefined} aria-hidden />
                        <span className="m7r-option-text">
                          <span>{option.label}</span>
                        </span>
                      </button>
                    );
                  })}
                  <label className="m7r-own">
                    <PencilLine aria-hidden strokeWidth={1.75} />
                    <input
                      value={line}
                      maxLength={160}
                      placeholder="Or describe the post in a line"
                      aria-label="Or describe the post in a line"
                      onChange={(e) => setLine(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key !== "Enter" || e.shiftKey) return;
                        e.preventDefault();
                        next();
                      }}
                    />
                  </label>
                </div>
              </>
            )}
          </div>
          <div className="m7r-card-actions">
            <button type="button" className="m7r-text-btn" onClick={onDone}>
              Skip
            </button>
            {tab === 0 ? (
              <button type="button" className="m7r-primary" onClick={() => setTab(1)}>
                Next
              </button>
            ) : (
              <button type="button" className="m7r-primary" disabled={!ready} onClick={next}>
                Next
              </button>
            )}
          </div>
        </section>
      </div>
    );
  }

  const quick = kind === "quick";
  return (
    <>
      {quickable ? (
        <div className="m7r-answered">
          <div className="m7r-answered-item">
            <p className="m7r-answered-q">{QUESTION}</p>
            <div className="m7r-answered-pick">
              <Check aria-hidden strokeWidth={2} />
              <span>{chosen?.label ?? KINDS[0].label}</span>
            </div>
          </div>
        </div>
      ) : null}
      <section className="m7r-card" data-live aria-label="The agent is asking">
        <div className="m7r-card-head">
          <p className="m7r-card-question">What is the post?</p>
          <p className="m7r-card-hint">A working title, its day and format</p>
        </div>
        <div className="px-4 pb-4">
          <AddPostForm
            bare
            year={year}
            month={month}
            day={day}
            initialTitle={line.trim()}
            // A quick post takes no pillar: it skips the planner's pillars entirely.
            pillars={quick ? undefined : pillars}
            submitLabel={quick ? "Add quick post" : "Add post"}
            slides={quick}
            titlePlaceholder={quick ? "A working title" : undefined}
            onAdd={quick && onAddQuick ? onAddQuick : onAdd}
            onClose={onDone}
          />
        </div>
      </section>
    </>
  );
}
